import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { auth } from '../firebase/config';
import { createCheckoutSession, getStripe, PLAN_TYPES, BILLING_PERIODS } from '../services/stripeService';
import { 
  ArrowRight, 
  Users, 
  QrCode, 
  CheckCircle,
  Star,
  Zap,
  Menu,
  X,
  Play,
  TrendingUp,
  Heart,
  BarChart3,
  Palette,
  LogOut,
  LayoutDashboard,
  Plus
} from 'lucide-react';
import { cn } from '../utils';
import LanguageSelector from '../components/LanguageSelector';

const LandingPage: React.FC = () => {
  // PROD 환경에서 콘솔 로그 비활성화
  if (process.env.NODE_ENV === 'production') {
    console.log = () => {};
    console.warn = () => {};
    // console.error는 유지하여 런타임 에러를 포착
  }
  
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { } = useSubscription();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [faqOpen, setFaqOpen] = useState<number | null>(1); // 기본 1번 FAQ 펼침

  // 스크롤 감지
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 부드러운 스크롤 함수
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = 48; // top-12 = 48px
      const elementPosition = element.offsetTop;
      const offsetPosition = Math.max(0, elementPosition - navHeight - 20); // 추가 여백 20px, 최소 0
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Free Trial 버튼 클릭 시 무료 명함 만들기 페이지로 이동
  const handleFreeTrial = () => {
    console.log('🔍 [LandingPage] handleFreeTrial 호출됨');
    console.log('🔍 [LandingPage] isAuthenticated:', isAuthenticated);
    console.log('🔍 [LandingPage] user:', user);
    console.log('🔍 [LandingPage] authLoading:', authLoading);
    
    try {
      // 인증 로딩 중이면 대기
      if (authLoading) {
        console.log('⏳ [LandingPage] 인증 로딩 중, 대기...');
        return;
      }
      
      // user 객체가 없으면 로그인하지 않은 것으로 판단
      if (!user) {
        console.log('❌ [LandingPage] 사용자 미로그인, 로그인 페이지로 이동 (로그인 후 명함 만들기로 리다이렉트)');
        // 로그인하지 않은 경우 로그인 페이지로 이동 (로그인 후 명함 만들기 페이지로 리다이렉트)
        navigate('/login', { state: { from: '/card/create' } });
      } else {
        console.log('✅ [LandingPage] 사용자 로그인됨, 명함 만들기 페이지로 이동');
        // 로그인한 경우 무료 명함 만들기 페이지로 이동
        navigate('/card/create');
      }
    } catch (error) {
      console.error('❌ [LandingPage] handleFreeTrial 에러:', error);
    }
  };

  // 다른 페이지에서 /#section 형태로 이동했을 때 자동 스크롤 처리
  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.replace('#', '');
      // 렌더 완료 후 부드러운 스크롤
      const timer = setTimeout(() => {
        scrollToSection(targetId);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  // 바로 Stripe 결제로 연결하는 함수
  const handleDirectPayment = async (planType: string, billingPeriod: string) => {
    try {
      setLoading(true);
      
      // 사용자 인증 확인
      if (!isAuthenticated) {
        // 로그인하지 않은 경우 로그인 페이지로 이동
        window.location.href = '/login';
        return;
      }

      // 플랜 타입과 빌링 주기 매핑
      const planMapping: { [key: string]: string } = {
        'card_only': PLAN_TYPES.CARD_ONLY,
        'standard': PLAN_TYPES.STANDARD,
        'pro': PLAN_TYPES.PRO
      };

      const billingMapping: { [key: string]: string } = {
        'monthly': BILLING_PERIODS.MONTHLY,
        'yearly': BILLING_PERIODS.YEARLY
      };

      const mappedPlan = planMapping[planType] || PLAN_TYPES.STANDARD;
      const mappedBilling = billingMapping[billingPeriod] || BILLING_PERIODS.MONTHLY;

      // 체크아웃 세션 생성
      const { sessionId } = await createCheckoutSession(
        mappedPlan as any,
        mappedBilling as any,
        auth.currentUser?.uid || ''
      );

      // Stripe Checkout으로 리다이렉트
      const stripe = await getStripe();
      if (stripe) {
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          console.error('Stripe checkout error:', error);
          alert(t('payment.error'));
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(t('payment.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      await auth.signOut();
    } catch (e: any) {
      console.error('로그아웃 실패:', e);
      alert(`로그아웃 실패: ${e.message || '알 수 없는 오류'}`);
    } finally {
      setLoading(false);
    }
  };




  // 가격 표시 상태 관리
  const [isYearly, setIsYearly] = useState(false);

  const plans = [
    {
      name: t('landing.plans.free.name'),
      price: t('landing.plans.free.price'),
      period: t('landing.plans.free.period'),
      features: [
        t('landing.plans.free.feature1'),
        t('landing.plans.free.feature2'),
        t('landing.plans.free.feature3'),
        t('landing.plans.free.feature4')
      ],
      popular: false,
      cta: t('landing.plans.free.cta'),
      isFree: true
    },
    {
      name: t('landing.plans.cardOnly.name'),
      price: isYearly ? 'US$30.00' : 'US$3.00',
      period: isYearly ? t('paywall.period.yearly') : t('paywall.period.monthly'),
      originalPrice: isYearly ? 'US$36.00' : null,
      features: [
        t('landing.plans.cardOnly.feature1'),
        t('landing.plans.cardOnly.feature2'),
        t('landing.plans.cardOnly.feature3'),
        t('landing.plans.cardOnly.feature4')
      ],
      popular: false,
      cta: t('landing.plans.cardOnly.cta')
    },
    {
      name: t('landing.plans.standard.name'),
      price: isYearly ? 'US$100.00' : 'US$10.00',
      period: isYearly ? t('paywall.period.yearly') : t('paywall.period.monthly'),
      originalPrice: isYearly ? 'US$120.00' : null,
      features: [
        t('landing.plans.standard.feature1'),
        t('landing.plans.standard.feature2'),
        t('landing.plans.standard.feature3'),
        t('landing.plans.standard.feature4')
      ],
      popular: true,
      cta: t('landing.plans.standard.cta')
    },
    {
      name: t('landing.plans.pro.name'),
      price: isYearly ? 'US$230.00' : 'US$23.00',
      period: isYearly ? t('paywall.period.yearly') : t('paywall.period.monthly'),
      originalPrice: isYearly ? 'US$276.00' : null,
      features: [
        t('landing.plans.pro.feature1'),
        t('landing.plans.pro.feature2'),
        t('landing.plans.pro.feature3'),
        t('landing.plans.pro.feature4'),
        t('landing.plans.pro.feature5')
      ],
      popular: false,
      cta: t('landing.plans.pro.cta')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Top Header Bar */}
      <div className="lux-ink border-b border-white/10 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Quick Links & Social Media Icons - Hidden on mobile */}
            <div className="hidden md:flex items-center space-x-4">
              <a href="https://twitter.com/scanvcf" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#c8a45e] transition-colors" aria-label="Twitter">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a href="https://facebook.com/scanvcf" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#c8a45e] transition-colors" aria-label="Facebook">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="https://linkedin.com/company/scanvcf" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#c8a45e] transition-colors" aria-label="LinkedIn">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="https://discord.gg/scanvcf" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#c8a45e] transition-colors" aria-label="Discord">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.781h-1.68V5.526h1.68v1.681zm-1.68 9.781c-1.297 0-2.448-.49-3.323-1.297-.928-.875-1.418-2.026-1.418-3.323s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297z"/>
                </svg>
              </a>
              <a href="https://youtube.com/@scanvcf" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-[#c8a45e] transition-colors" aria-label="YouTube">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>

            {/* User Actions */}
            <div className="flex items-center space-x-2 md:space-x-4">
              <LanguageSelector variant="button" />
              
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-sm font-medium"
                  >
                    <LayoutDashboard className="h-4 w-4 mr-1" />
                    <span>{t('navigation.dashboard')}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-sm font-medium"
                    disabled={loading}
                  >
                    <LogOut className="h-4 w-4 mr-1" />
                    <span>{t('auth.logout')}</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/register"
                    state={{ from: '/dashboard' }}
                    className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-sm font-medium"
                  >
                    <Users className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">{t('authForm.title.signUp')}</span>
                    <span className="sm:hidden">{t('authForm.title.signUp')}</span>
                  </Link>
                  <Link
                    to="/login"
                    state={{ from: '/dashboard' }}
                    className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-sm font-medium"
                  >
                    <ArrowRight className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">{t('authForm.title.signIn')}</span>
                    <span className="sm:hidden">{t('authForm.title.signIn')}</span>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Main Navigation */}
      <nav className={cn(
        "fixed left-0 right-0 z-40 transition-all duration-300",
        "top-12", // 상단 헤더 높이만큼 아래로 이동
        isScrolled ? "bg-white/95 backdrop-blur-md border-b border-gray-200/70 shadow-sm" : "bg-white md:bg-transparent"
      )}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <img
                src="/scanvcf_icon_transparent_pack/scanvcf_icon_square_transparent_32.png"
                alt="ScanVCF"
                className="w-8 h-8 rounded-lg"
              />
              <span className={cn(
                "text-xl font-bold tracking-tight transition-colors",
                isScrolled ? "text-gray-900" : "text-gray-900 md:text-white"
              )}>ScanVCF</span>
            </div>
            
            {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
  <button 
    onClick={() => scrollToSection('home')}
    className={`transition-colors ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'} text-sm font-medium tracking-wide`}
  >
    {t('navigation.home')}
  </button>
  <button 
    onClick={() => scrollToSection('benefits')}
    className={`transition-colors ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'} text-sm font-medium tracking-wide`}
  >
    {t('navigation.benefits')}
  </button>
  <Link 
    to="/features" 
    className={`transition-colors ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'} text-sm font-medium tracking-wide`}
  >
    {t('navigation.features')}
  </Link>
  <button 
    onClick={() => scrollToSection('how-it-works')}
    className={`transition-colors ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'} text-sm font-medium tracking-wide`}
  >
    {t('navigation.howItWorks')}
  </button>
  <button 
    onClick={() => scrollToSection('pricing')}
    className={`transition-colors ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'} text-sm font-medium tracking-wide`}
  >
    {t('navigation.pricing')}
  </button>
  <Link 
    to="/contact"
    className={`transition-colors ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'} text-sm font-medium tracking-wide`}
  >
    {t('navigation.contact')}
  </Link>
</div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-2 space-y-1">
              <button onClick={() => { scrollToSection('home'); setIsMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 text-gray-600 hover:text-primary-600">
                {t('navigation.home')}
              </button>
              <button onClick={() => { scrollToSection('benefits'); setIsMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 text-gray-600 hover:text-primary-600">
                {t('navigation.benefits')}
              </button>
              <Link to="/features" className="block px-3 py-2 text-gray-600 hover:text-primary-600" onClick={() => setIsMobileMenuOpen(false)}>
                {t('navigation.features')}
              </Link>
              <button onClick={() => { scrollToSection('how-it-works'); setIsMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 text-gray-600 hover:text-primary-600">
                {t('navigation.howItWorks')}
              </button>
              <button onClick={() => { scrollToSection('pricing'); setIsMobileMenuOpen(false); }} className="block w-full text-left px-3 py-2 text-gray-600 hover:text-primary-600">
                {t('navigation.pricing')}
              </button>
              <Link 
                to="/contact" 
                className="block px-3 py-2 text-gray-600 hover:text-primary-600" 
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navigation.contact')}
              </Link>
              {/* Mobile User Actions */}
              <div className="px-3 py-2 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <LanguageSelector variant="mobile" />
                </div>
                {isAuthenticated ? (
                  <div className="space-y-2">
                    <Link
                      to="/dashboard"
                      className="flex items-center justify-center w-full px-4 py-2 text-gray-600 hover:text-primary-600 font-medium border border-gray-300 rounded-lg"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      {t('navigation.dashboard')}
                    </Link>
                    <Link
                      to="/settings"
                      className="flex items-center justify-center w-full px-4 py-2 text-gray-600 hover:text-primary-600 font-medium border border-gray-300 rounded-lg"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {t('navigation.settings')}
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center justify-center w-full px-4 py-2 text-gray-600 hover:text-primary-600 font-medium border border-gray-300 rounded-lg"
                      disabled={loading}
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('auth.logout')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Link
                      to="/login"
                      className="flex items-center justify-center w-full px-4 py-2 text-gray-600 hover:text-primary-600 font-medium border border-gray-300 rounded-lg"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />
                      {t('auth.login')}
                    </Link>
                    <Link
                      to="/register"
                      className="flex items-center justify-center w-full px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 font-medium rounded-lg"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Users className="h-4 w-4 mr-2" />
                      {t('authForm.title.signUp')}
                    </Link>
                  </div>
                )}
              </div>
              
              {/* Android App Banner */}
              <div className="px-3 py-2 border-t border-gray-200">
                <div className="lux-ink rounded-lg p-4 text-white border border-[#c8a45e]/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <svg className="h-8 w-8 mr-3" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                      </svg>
                      <div>
                        <h3 className="font-semibold text-sm">Android 앱 다운로드</h3>
                        <p className="text-white/60 text-xs">모바일에서 더 편리하게</p>
                      </div>
                    </div>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.scanvcf.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="lux-btn-gold px-3 py-1.5 rounded-full text-xs font-semibold"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      다운로드
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Modern Style - Compact */}
      <section id="home" className="modern-hero min-h-[calc(100vh-4rem)] flex items-center justify-center pt-16 md:pt-0 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-20">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left pt-8 md:pt-0 relative z-10">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-5 md:mb-7 leading-tight">
                {t('landing.hero.newTitle')}
              </h1>

              <p className="text-base md:text-lg text-white/60 mb-8 md:mb-10 max-w-2xl leading-relaxed">
                {t('landing.hero.newSubtitle')}
              </p>
              
              {/* Main CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 md:gap-6 mb-8 md:mb-10 relative z-10">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    console.log('Start Now button clicked');
                    handleFreeTrial();
                  }}
                  disabled={loading}
                  className="lux-btn-gold font-semibold py-4 px-9 rounded-full inline-flex items-center justify-center text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('common.loading') : t('landing.hero.startNow')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    console.log('Watch Demo button clicked');
                    window.open('https://www.youtube.com/watch?v=scanvcf-demo', '_blank', 'noopener,noreferrer');
                  }}
                  className="lux-btn-ghost font-semibold py-4 px-9 rounded-full inline-flex items-center justify-center text-base"
                >
                  <Play className="mr-2 h-5 w-5" />
                  {t('landing.hero.watchDemo')}
                </button>
              </div>
              
              {/* Trust Elements */}
              <div className="flex flex-wrap justify-center lg:justify-start items-center gap-4 md:gap-6 text-sm text-white/80 mb-8">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2 text-[#c8a45e]" />
                  <span>{t('landing.trust.freeStart')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2 text-[#c8a45e]" />
                  <span>{t('landing.trust.noInstall')}</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2 text-[#c8a45e]" />
                  <span>{t('landing.trust.multiPlatform')}</span>
                </div>
              </div>
              
              {/* Social Proof */}
              <div className="text-center lg:text-left">
                <p className="text-white/70 text-sm">
                  {t('landing.hero.socialProof')}
                </p>
              </div>
            </div>
            
            {/* Right Content - Video/Image */}
            <div className="relative max-w-md mx-auto lg:mx-0">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_60px_rgba(0,0,0,0.45)]">
                <div className="aspect-video bg-gradient-to-br from-[#141b30] to-[#0a0e1a] flex items-center justify-center">
                  <button
                    onClick={() => window.open('https://youtu.be/scanvcf-demo', '_blank', 'noopener,noreferrer')}
                    className="w-16 h-16 rounded-full flex items-center justify-center border border-[#c8a45e]/60 bg-[#c8a45e]/10 hover:bg-[#c8a45e]/25 transition-all duration-300 transform hover:scale-110"
                  >
                    <Play className="w-6 h-6 ml-1 text-[#c8a45e]" />
                  </button>
                </div>
                <div className="absolute bottom-2 left-2">
                  <p className="text-white/80 text-xs font-medium bg-black/50 backdrop-blur-sm rounded px-2 py-1">
                    {t('landing.hero.videoCaption')}
                  </p>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#c8a45e] rounded-full opacity-10 blur-2xl"></div>
              <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-blue-500 rounded-full opacity-10 blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>


      {/* Key Benefits Section */}
      <section id="benefits" className="py-24 bg-white scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              {t('landing.benefits.title')}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            <div 
              className="text-center cursor-pointer group hover:scale-105 transition-transform duration-300"
              onClick={() => navigate('/features')}
            >
              <div className="lux-icon-tile mx-auto mb-6 group-hover:scale-105 transition-transform">
                <QrCode className="h-7 w-7 stroke-1" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('landing.benefits.instant.title')}
              </h3>
              <p className="text-gray-600">
                {t('landing.benefits.instant.description')}
              </p>
            </div>
            
            <div 
              className="text-center cursor-pointer group hover:scale-105 transition-transform duration-300"
              onClick={() => navigate('/features')}
            >
              <div className="lux-icon-tile mx-auto mb-6 group-hover:scale-105 transition-transform">
                <Palette className="h-7 w-7 stroke-1" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('landing.benefits.design.title')}
              </h3>
              <p className="text-gray-600">
                {t('landing.benefits.design.description')}
              </p>
            </div>
            
            <div 
              className="text-center cursor-pointer group hover:scale-105 transition-transform duration-300"
              onClick={() => navigate('/features')}
            >
              <div className="lux-icon-tile mx-auto mb-6 group-hover:scale-105 transition-transform">
                <BarChart3 className="h-7 w-7 stroke-1" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('landing.benefits.business.title')}
              </h3>
              <p className="text-gray-600">
                {t('landing.benefits.business.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-24 lux-dark-section relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
              {t('landing.target.title')}
            </h2>
            <p className="text-white/55 text-lg max-w-3xl mx-auto leading-relaxed">
              {t('landing.target.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              className="lux-dark-card p-6 text-center cursor-pointer group"
              onClick={() => navigate('/features')}
            >
              <div className="w-14 h-14 rounded-2xl border border-[#c8a45e]/35 bg-[#c8a45e]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#c8a45e]/20 transition-colors">
                <Users className="h-7 w-7 text-[#c8a45e] stroke-1" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {t('landing.target.freelancer')}
              </h3>
              <p className="text-white/55 mb-4 text-sm leading-relaxed">
                {t('landing.target.freelancer.desc')}
              </p>
              <div className="text-sm text-[#c8a45e] underline decoration-[#c8a45e]/40 underline-offset-4 group-hover:decoration-[#c8a45e]">
                {t('landing.target.sampleLink')}
              </div>
            </div>
            
            <div 
              className="lux-dark-card p-6 text-center cursor-pointer group"
              onClick={() => navigate('/features')}
            >
              <div className="w-14 h-14 rounded-2xl border border-[#c8a45e]/35 bg-[#c8a45e]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#c8a45e]/20 transition-colors">
                <TrendingUp className="h-7 w-7 text-[#c8a45e] stroke-1" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {t('landing.target.smallbiz')}
              </h3>
              <p className="text-white/55 mb-4 text-sm leading-relaxed">
                {t('landing.target.smallbiz.desc')}
              </p>
              <div className="text-sm text-[#c8a45e] underline decoration-[#c8a45e]/40 underline-offset-4 group-hover:decoration-[#c8a45e]">
                {t('landing.target.sampleLink')}
              </div>
            </div>
            
            <div 
              className="lux-dark-card p-6 text-center cursor-pointer group"
              onClick={() => navigate('/features')}
            >
              <div className="w-14 h-14 rounded-2xl border border-[#c8a45e]/35 bg-[#c8a45e]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#c8a45e]/20 transition-colors">
                <Star className="h-7 w-7 text-[#c8a45e] stroke-1" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {t('landing.target.restaurant')}
              </h3>
              <p className="text-white/55 mb-4 text-sm leading-relaxed">
                {t('landing.target.restaurant.desc')}
              </p>
              <div className="text-sm text-[#c8a45e] underline decoration-[#c8a45e]/40 underline-offset-4 group-hover:decoration-[#c8a45e]">
                {t('landing.target.sampleLink')}
              </div>
            </div>
            
            <div 
              className="lux-dark-card p-6 text-center cursor-pointer group"
              onClick={() => navigate('/features')}
            >
              <div className="w-14 h-14 rounded-2xl border border-[#c8a45e]/35 bg-[#c8a45e]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#c8a45e]/20 transition-colors">
                <Heart className="h-7 w-7 text-[#c8a45e] stroke-1" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {t('landing.target.education')}
              </h3>
              <p className="text-white/55 mb-4 text-sm leading-relaxed">
                {t('landing.target.education.desc')}
              </p>
              <div className="text-sm text-[#c8a45e] underline decoration-[#c8a45e]/40 underline-offset-4 group-hover:decoration-[#c8a45e]">
                {t('landing.target.sampleLink')}
              </div>
            </div>
            
            <div 
              className="lux-dark-card p-6 text-center cursor-pointer group"
              onClick={() => navigate('/features')}
            >
              <div className="w-14 h-14 rounded-2xl border border-[#c8a45e]/35 bg-[#c8a45e]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#c8a45e]/20 transition-colors">
                <Zap className="h-7 w-7 text-[#c8a45e] stroke-1" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {t('landing.target.creator')}
              </h3>
              <p className="text-white/55 mb-4 text-sm leading-relaxed">
                {t('landing.target.creator.desc')}
              </p>
              <div className="text-sm text-[#c8a45e] underline decoration-[#c8a45e]/40 underline-offset-4 group-hover:decoration-[#c8a45e]">
                {t('landing.target.sampleLink')}
              </div>
            </div>
            
            <div 
              className="lux-dark-card p-6 text-center cursor-pointer group"
              onClick={() => navigate('/features')}
            >
              <div className="w-14 h-14 rounded-2xl border border-[#c8a45e]/35 bg-[#c8a45e]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#c8a45e]/20 transition-colors">
                <BarChart3 className="h-7 w-7 text-[#c8a45e] stroke-1" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {t('landing.target.networking')}
              </h3>
              <p className="text-white/55 mb-4 text-sm leading-relaxed">
                {t('landing.target.networking.desc')}
              </p>
              <div className="text-sm text-[#c8a45e] underline decoration-[#c8a45e]/40 underline-offset-4 group-hover:decoration-[#c8a45e]">
                {t('landing.target.sampleLink')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              {t('landing.faq.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('landing.faq.subtitle')}
            </p>
          </div>

          <div className="space-y-4">
            <div className="lux-card overflow-hidden">
              <button 
                className="w-full text-left p-6 focus:outline-none"
                onClick={() => setFaqOpen(faqOpen === 1 ? null : 1)}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold tracking-tight text-gray-900">
                    {t('landing.faq.q1')}
                  </h3>
                  <div className={`text-gray-400 transform transition-transform duration-200 ${faqOpen === 1 ? 'rotate-180' : ''}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </button>
              {faqOpen === 1 && (
                <div className="px-6 pb-6">
                  <p className="text-gray-600">
                    {t('landing.faq.a1')}
                  </p>
                </div>
              )}
            </div>
            
            <div className="lux-card overflow-hidden">
              <button 
                className="w-full text-left p-6 focus:outline-none"
                onClick={() => setFaqOpen(faqOpen === 2 ? null : 2)}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold tracking-tight text-gray-900">
                    {t('landing.faq.q2')}
                  </h3>
                  <div className={`text-gray-400 transform transition-transform duration-200 ${faqOpen === 2 ? 'rotate-180' : ''}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </button>
              {faqOpen === 2 && (
                <div className="px-6 pb-6">
                  <p className="text-gray-600">
                    {t('landing.faq.a2')}
                  </p>
                </div>
              )}
            </div>
            
            <div className="lux-card overflow-hidden">
              <button 
                className="w-full text-left p-6 focus:outline-none"
                onClick={() => setFaqOpen(faqOpen === 3 ? null : 3)}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold tracking-tight text-gray-900">
                    {t('landing.faq.q3')}
                  </h3>
                  <div className={`text-gray-400 transform transition-transform duration-200 ${faqOpen === 3 ? 'rotate-180' : ''}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </button>
              {faqOpen === 3 && (
                <div className="px-6 pb-6">
                  <p className="text-gray-600">
                    {t('landing.faq.a3')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

     

        {/* How It Works Section - Business Card Profile Creation */}
      <section id="how-it-works" className="py-24 lux-ivory scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="lux-eyebrow">
              {t('landing.howto.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mt-2 mb-4">
              {t('landing.howitworks.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('landing.howitworks.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="lux-step-number mb-6">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('landing.howitworks.step1.title')}
              </h3>
              <p className="text-gray-600">
                {t('landing.howitworks.step1.description')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="lux-step-number mb-6">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('landing.howitworks.step2.title')}
              </h3>
              <p className="text-gray-600">
                {t('landing.howitworks.step2.description')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="lux-step-number mb-6">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('landing.howitworks.step3.title')}
              </h3>
              <p className="text-gray-600">
                {t('landing.howitworks.step3.description')}
              </p>
            </div>
          </div>
          
          {/* How It Works Video Section */}
          <div className="mt-16">
            <div className="relative max-w-4xl mx-auto">
              <div className="aspect-video rounded-2xl overflow-hidden border border-gray-200 shadow-[0_30px_60px_rgba(15,23,42,0.15)] bg-gradient-to-br from-[#141b30] to-[#0a0e1a] flex items-center justify-center">
                <button
                  onClick={() => window.open('https://youtu.be/scanvcf-how-it-works', '_blank', 'noopener,noreferrer')}
                  className="w-20 h-20 rounded-full flex items-center justify-center border border-[#c8a45e]/60 bg-[#c8a45e]/10 hover:bg-[#c8a45e]/25 transition-all duration-300 transform hover:scale-110"
                >
                  <Play className="w-8 h-8 ml-1 text-[#c8a45e]" />
                </button>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white text-sm font-medium bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
                  {t('landing.howitworks.watchVideo')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="lux-eyebrow">
              {t('landing.pricing.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mt-2 mb-4">
              {t('landing.pricing.title')}
            </h2>
            <p className="text-xl text-gray-600 mb-4">
              {t('landing.pricing.subtitle')}
            </p>
            <p className="text-sm text-gray-500">
              {t('landing.pricing.currencyNote')}
            </p>
            
            {/* Billing Toggle */}
            <div className="flex items-center justify-center space-x-4 mb-8">
              <span className={`text-sm font-medium capitalize ${!isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
                {t('paywall.period.monthly')}
              </span>
              <button
                onClick={() => setIsYearly(!isYearly)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#c8a45e] focus:ring-offset-2 ${
                  isYearly ? 'bg-gray-900' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isYearly ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium capitalize ${isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
                {t('paywall.period.yearly')}
              </span>
              {isYearly && (
                <span className="border border-[#c8a45e]/40 bg-[#c8a45e]/10 text-[#96742f] text-xs font-semibold px-2.5 py-0.5 rounded-full">
                  {t('pricing.save', { percentage: '20' })}
                </span>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={cn(
                  "rounded-2xl p-8 relative transition-all duration-300",
                  plan.popular
                    ? "lux-ink text-white border border-[#c8a45e]/50 shadow-[0_30px_60px_rgba(10,14,26,0.35)] transform lg:scale-105 pt-6 md:pt-10"
                    : "bg-white border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1"
                )}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="lux-btn-gold px-6 py-2 rounded-full text-sm font-semibold">
                      <div className="flex items-center space-x-2">
                        <Star className="h-4 w-4" />
                        <span>{t('landing.pricing.mostPopular')}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="text-center">
                  <h3 className={cn(
                    "text-lg font-semibold tracking-tight mb-2",
                    plan.popular ? "text-white" : "text-gray-900"
                  )}>{plan.name}</h3>
                  <div className="mb-6">
                    {plan.originalPrice && (
                      <div className={cn(
                        "text-sm line-through mb-1",
                        plan.popular ? "text-white/40" : "text-gray-400"
                      )}>
                        {plan.originalPrice}{t('paywall.perYear')}
                      </div>
                    )}
                    <div className="flex items-baseline justify-center">
                      <span className={cn(
                        "text-3xl font-bold tracking-tight",
                        plan.popular ? "text-white" : "text-gray-900"
                      )}>{plan.price}</span>
                      <span className={cn(
                        "ml-1 text-sm",
                        plan.popular ? "text-white/50" : "text-gray-500"
                      )}>{plan.period}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8 text-left">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#c8a45e] mr-3 flex-shrink-0 mt-0.5" />
                        <span className={cn(
                          "text-sm leading-relaxed",
                          plan.popular ? "text-white/70" : "text-gray-600"
                        )}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => {
                      if ((plan as any).isFree) {
                        handleFreeTrial();
                      } else {
                        handleDirectPayment(plan.name.toLowerCase().replace(' ', '_'), isYearly ? 'yearly' : 'monthly');
                      }
                    }}
                    disabled={loading}
                    className={cn(
                      "w-full inline-flex items-center justify-center px-6 py-3 text-base font-semibold rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                      plan.popular
                        ? "lux-btn-gold"
                        : "lux-btn-outline"
                    )}
                  >
                    {loading ? '처리 중...' : plan.cta}
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Pricing Note */}
          <div className="text-center mt-12">
            <p className="text-sm text-gray-500">
              {t('landing.pricing.note')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-24 lux-dark-section relative overflow-hidden scroll-mt-28">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
            {t('landing.cta.title')}
          </h2>
          <p className="text-lg text-white/55 mb-10 max-w-3xl mx-auto leading-relaxed">
            {t('landing.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleFreeTrial}
              disabled={loading}
              className="lux-btn-gold inline-flex items-center justify-center px-9 py-4 text-base font-semibold rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('common.loading') : t('landing.cta.button')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <button
              onClick={() => scrollToSection('pricing')}
              disabled={loading}
              className="lux-btn-ghost inline-flex items-center justify-center px-9 py-4 text-base font-semibold rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('common.loading') : t('landing.cta.subscribe')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="lux-ink border-t border-white/10 text-white py-16 scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <img
                  src="/scanvcf_icon_transparent_pack/scanvcf_icon_square_transparent_32.png"
                  alt="ScanVCF"
                  className="w-8 h-8 rounded-lg"
                />
                <span className="text-xl font-bold">ScanVCF</span>
              </div>
              <p className="text-gray-400 mb-6">
                {t('landing.footer.description')}
              </p>
              <div className="flex space-x-4">
                <a href="https://twitter.com/scanvcf" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#c8a45e] transition-colors" aria-label="Twitter">
                  <span className="sr-only">Twitter</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a href="https://facebook.com/scanvcf" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#c8a45e] transition-colors" aria-label="Facebook">
                  <span className="sr-only">Facebook</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="https://instagram.com/scanvcf" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#c8a45e] transition-colors" aria-label="Instagram">
                  <span className="sr-only">Instagram</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.781h-1.68V5.526h1.68v1.681zm-1.68 9.781c-1.297 0-2.448-.49-3.323-1.297-.928-.875-1.418-2.026-1.418-3.323s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">{t('landing.footer.product')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/features" className="hover:text-white transition-colors">{t('landing.footer.features')}</Link></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors text-left">{t('landing.footer.pricing')}</button></li>
                <li><a href="https://play.google.com/store/apps/details?id=com.scanvcf.app" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{t('landing.footer.download')}</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">{t('landing.footer.support')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/help" className="hover:text-white transition-colors">{t('landing.footer.help')}</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">{t('landing.footer.contact')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">{t('landing.footer.legal')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/privacy.html" className="hover:text-white transition-colors">{t('landing.footer.privacy')}</a></li>
                <li><a href="/terms.html" className="hover:text-white transition-colors">{t('landing.footer.terms')}</a></li>
                <li><a href="/delete-account.html" className="hover:text-white transition-colors">{t('landing.footer.delete')}</a></li>
                <li><a href="/refund-policy.html" className="hover:text-white transition-colors">{t('landing.footer.refund')}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                &copy; 2025 ScanVCF. All rights reserved.
              </p>
              <div className="flex items-center space-x-4 mt-4 md:mt-0">
                <span className="text-gray-400 text-sm">
                  {t('landing.footer.madeWith')} <Heart className="inline w-4 h-4 text-red-500" /> {t('landing.footer.by')} <a href="https://t2design.com.au/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">T2DESIGN</a>
                </span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col space-y-3">
        {/* Create Card FAB */}
        <button
          onClick={handleFreeTrial}
          className="lux-btn-gold p-4 rounded-full flex items-center justify-center group relative"
          title={t('landing.fab.createCard')}
        >
          <Plus className="h-6 w-6" />
          <span className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 text-sm font-medium bg-gray-900 text-white px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            {t('landing.fab.createCard')}
          </span>
        </button>

        {/* Dashboard FAB */}
        {isAuthenticated && (
          <Link
            to="/dashboard"
            className="bg-gray-600 hover:bg-gray-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center group relative"
            title={t('navigation.dashboard')}
          >
            <LayoutDashboard className="h-6 w-6" />
            <span className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 text-sm font-medium bg-gray-900 text-white px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              {t('navigation.dashboard')}
            </span>
          </Link>
        )}
      </div>
    </div>
  );
};

export default LandingPage; 